import React, { useState, useEffect } from 'react';
import { API_ORIGIN } from '../config/env';
import { authFetch } from '../config/api';
import { toast } from 'react-toastify';
import { celebrationsService } from '../services/celebrationsService';
import './MessageBoard.css';

// =====================================================
// GVC CELEBRATIONS BOARD - Admin Page
// Manage Google Reviews Contest & Birthday Celebrations
// =====================================================

// API origin comes from config/env.js (P1-B)
const getApiBase = () => API_ORIGIN;

const CelebrationsBoard = () => {
  const [activeTab, setActiveTab] = useState('contest');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Contest State
  const [contests, setContests] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showContestModal, setShowContestModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  
  // Print state
  const [printingAll, setPrintingAll] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  
  // Birthday State
  const [birthdays, setBirthdays] = useState([]);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [editingBirthday, setEditingBirthday] = useState(null);
  
  // Form State
  const [contestForm, setContestForm] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    prize_1st: '$500',
    prize_2nd: '$250',
    prize_3rd: '$100',
    google_place_id: ''
  });
  
  const [birthdayForm, setBirthdayForm] = useState({
    employee_name: '',
    birthday_date: '',
    message: '',
    display_from: '',
    display_until: '',
    priority: 'normal'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, contestsData, participantsData, reviewsData, birthdaysData] = await Promise.all([
        celebrationsService.getStats(),
        celebrationsService.getContests(),
        celebrationsService.getParticipants(),
        celebrationsService.getReviews(),
        celebrationsService.getBirthdays()
      ]);
      setStats(statsData.stats);
      setContests(contestsData.contests || []);
      setParticipants(participantsData.participants || []);
      setReviews(reviewsData.reviews || []);
      setBirthdays(birthdaysData.birthdays || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ===== QR CARD PRINT HANDLERS =====
  const handlePrintAllQR = async () => {
    setPrintingAll(true);
    try {
      const response = await authFetch(`${getApiBase()}/api/celebrations/qr-cards`);
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'GVC-QR-Cards-All.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`QR Cards PDF downloaded for ${participants.length} employees!`);
    } catch (error) {
      console.error('Print all error:', error);
      toast.error('Failed to generate QR cards PDF');
    } finally {
      setPrintingAll(false);
    }
  };

  const handlePrintSingleQR = async (participant) => {
    setPrintingId(participant.id);
    try {
      const response = await authFetch(`${getApiBase()}/api/celebrations/qr-cards/${participant.id}`);
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GVC-QR-Card-${participant.first_name}-${(participant.last_name || '').charAt(0)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Print single error:', error);
      toast.error('Failed to generate QR card');
    } finally {
      setPrintingId(null);
    }
  };

  const handleCreateContest = async (e) => {
    e.preventDefault();
    try {
      await celebrationsService.createContest(contestForm);
      toast.success('Contest created!');
      setShowContestModal(false);
      setContestForm({ name: '', description: '', start_date: '', end_date: '', prize_1st: '$500', prize_2nd: '$250', prize_3rd: '$100', google_place_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create contest');
    }
  };

  const handleCreateBirthday = async (e) => {
    e.preventDefault();
    try {
      if (editingBirthday) {
        await celebrationsService.updateBirthday(editingBirthday.id, birthdayForm);
        toast.success('Birthday updated!');
      } else {
        await celebrationsService.createBirthday(birthdayForm);
        toast.success('Birthday added!');
      }
      setShowBirthdayModal(false);
      setEditingBirthday(null);
      setBirthdayForm({ employee_name: '', birthday_date: '', message: '', display_from: '', display_until: '', priority: 'normal' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save birthday');
    }
  };

  const handleDeleteBirthday = async (id) => {
    if (!window.confirm('Delete this birthday?')) return;
    try {
      await celebrationsService.deleteBirthday(id);
      toast.success('Birthday deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete birthday');
    }
  };

  const handleEditBirthday = (birthday) => {
    setEditingBirthday(birthday);
    setBirthdayForm({
      employee_name: birthday.employee_name,
      birthday_date: birthday.birthday_date?.split('T')[0] || '',
      message: birthday.message || '',
      display_from: birthday.display_from?.split('T')[0] || '',
      display_until: birthday.display_until?.split('T')[0] || '',
      priority: birthday.priority || 'normal'
    });
    setShowBirthdayModal(true);
  };

  const handleVerifyReview = async (reviewId, participantId) => {
    try {
      await celebrationsService.attributeReview(reviewId, { participant_id: participantId, status: 'verified' });
      toast.success('Review verified!');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify review');
    }
  };

  const openPiDisplay = () => {
    window.open('/celebrations-display', '_blank');
  };

  if (loading) {
    return (
      <div className="message-board-loading">
        <div className="loading-spinner">🎉</div>
        <p>Loading Celebrations...</p>
      </div>
    );
  }

  return (
    <div className="message-board-page">
      <div className="message-board-header">
        <div className="header-left">
          <span style={{ fontSize: 36 }}>🎉</span>
          <div>
            <h1>Celebrations Board</h1>
            <p>Manage contests, reviews, and birthday celebrations</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={openPiDisplay}>
            🖥️ Open Pi Display
          </button>
          {activeTab === 'contest' && participants.length > 0 && (
            <button
              className="btn-secondary"
              onClick={handlePrintAllQR}
              disabled={printingAll}
              style={{ background: '#1E5631', color: 'white' }}
            >
              {printingAll ? '⏳ Generating...' : '🖨️ Print All QR Cards'}
            </button>
          )}
          {activeTab === 'contest' && (
            <button className="btn-primary" onClick={() => setShowContestModal(true)}>
              + New Contest
            </button>
          )}
          {activeTab === 'birthdays' && (
            <button className="btn-primary" onClick={() => {
              setEditingBirthday(null);
              setBirthdayForm({ employee_name: '', birthday_date: '', message: '', display_from: '', display_until: '', priority: 'normal' });
              setShowBirthdayModal(true);
            }}>
              + Add Birthday
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-yellow">
          <div className="stat-icon">🏆</div>
          <div className="stat-value">{stats?.total_participants || 0}</div>
          <div className="stat-label">Participants</div>
        </div>
        <div className="stat-card stat-green">
          <div className="stat-icon">✅</div>
          <div className="stat-value">{stats?.verified_reviews || 0}</div>
          <div className="stat-label">Verified Reviews</div>
        </div>
        <div className="stat-card stat-blue">
          <div className="stat-icon">⏳</div>
          <div className="stat-value">{stats?.pending_reviews || 0}</div>
          <div className="stat-label">Pending Reviews</div>
        </div>
        <div className="stat-card stat-pink">
          <div className="stat-icon">🎂</div>
          <div className="stat-value">{stats?.today_birthdays || 0}</div>
          <div className="stat-label">Today's Birthdays</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-navigation">
        <button className={`tab-btn ${activeTab === 'contest' ? 'active' : ''}`} onClick={() => setActiveTab('contest')}>
          🏆 Contest ({participants.length})
        </button>
        <button className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
          ⭐ Reviews ({reviews.length})
        </button>
        <button className={`tab-btn ${activeTab === 'birthdays' ? 'active' : ''}`} onClick={() => setActiveTab('birthdays')}>
          🎂 Birthdays ({birthdays.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Contest Tab */}
        {activeTab === 'contest' && (
          <div className="contest-section">
            {/* Active Contest */}
            {contests.filter(c => c.is_active).length > 0 && (
              <div className="active-contest-card">
                {contests.filter(c => c.is_active).map(contest => (
                  <div key={contest.id} className="contest-info">
                    <h3>🏆 {contest.name}</h3>
                    <p>{contest.description}</p>
                    <div className="contest-meta">
                      <span>📅 {new Date(contest.start_date).toLocaleDateString()} - {new Date(contest.end_date).toLocaleDateString()}</span>
                      <span>👥 {contest.participant_count || 0} participants</span>
                    </div>
                    <div className="prize-info">
                      <span>🥇 {contest.prize_1st}</span>
                      <span>🥈 {contest.prize_2nd}</span>
                      <span>🥉 {contest.prize_3rd}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Leaderboard */}
            <div className="leaderboard-section">
              <h3>📊 Leaderboard</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Slug</th>
                    <th>Clicks</th>
                    <th>Verified</th>
                    <th>Pending</th>
                    <th>Avg Rating</th>
                    <th>QR Card</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="empty-cell">No participants yet</td>
                    </tr>
                  ) : (
                    participants.map((p, index) => (
                      <tr key={p.id}>
                        <td>
                          <span className={`rank-badge rank-${index + 1}`}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                          </span>
                        </td>
                        <td>{p.first_name} {p.last_name}</td>
                        <td>{p.phone}</td>
                        <td><code className="slug-code">{p.slug}</code></td>
                        <td>{p.click_count || 0}</td>
                        <td><span className="badge badge-green">{p.verified_reviews || 0}</span></td>
                        <td><span className="badge badge-yellow">{p.pending_reviews || 0}</span></td>
                        <td>{p.avg_rating > 0 ? `${Number(p.avg_rating).toFixed(1)} ⭐` : '-'}</td>
                        <td>
                          <button
                            className="btn-icon"
                            onClick={() => handlePrintSingleQR(p)}
                            disabled={printingId === p.id}
                            title={`Print QR card for ${p.first_name}`}
                            style={{ fontSize: 18 }}
                          >
                            {printingId === p.id ? '⏳' : '🖨️'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="reviews-section">
            <div className="reviews-header">
              <h3>⭐ Google Reviews</h3>
              <button className="btn-secondary" onClick={() => setShowReviewModal(true)}>
                + Add Review Manually
              </button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Author</th>
                  <th>Rating</th>
                  <th>Review</th>
                  <th>Date</th>
                  <th>Attribution</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-cell">No reviews yet</td>
                  </tr>
                ) : (
                  reviews.map(review => (
                    <tr key={review.id}>
                      <td>{review.author_name}</td>
                      <td>{'⭐'.repeat(review.rating)}</td>
                      <td className="review-text">{review.review_text?.substring(0, 100)}...</td>
                      <td>{review.review_time ? new Date(review.review_time).toLocaleDateString() : '-'}</td>
                      <td>
                        {review.first_name ? `${review.first_name} ${review.last_name}` : 
                          <select className="attribution-select" onChange={(e) => e.target.value && handleVerifyReview(review.id, e.target.value)} defaultValue="">
                            <option value="">Select...</option>
                            {participants.map(p => (
                              <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                            ))}
                          </select>
                        }
                      </td>
                      <td><span className={`status-badge status-${review.attribution_status}`}>{review.attribution_status}</span></td>
                      <td>
                        {review.attribution_status === 'pending' && review.attributed_participant_id && (
                          <button className="btn-sm btn-success" onClick={() => handleVerifyReview(review.id, review.attributed_participant_id)}>✓ Verify</button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Birthdays Tab */}
        {activeTab === 'birthdays' && (
          <div className="birthdays-section">
            <div className="birthday-cards">
              {birthdays.length === 0 ? (
                <div className="empty-state">
                  <span style={{ fontSize: 80 }}>🎂</span>
                  <h3>No birthdays scheduled</h3>
                  <p>Add employee birthdays to celebrate!</p>
                </div>
              ) : (
                birthdays.map(birthday => {
                  const isToday = new Date(birthday.birthday_date).toDateString() === new Date().toDateString();
                  return (
                    <div key={birthday.id} className={`birthday-card ${isToday ? 'birthday-today' : ''}`}>
                      <div className="birthday-emoji">{isToday ? '🎉' : '🎂'}</div>
                      <div className="birthday-info">
                        <h4>{birthday.employee_name}</h4>
                        <p className="birthday-date">
                          📅 {new Date(birthday.birthday_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </p>
                        {birthday.message && <p className="birthday-message">"{birthday.message}"</p>}
                        {isToday && <span className="today-badge">🎈 TODAY!</span>}
                      </div>
                      <div className="birthday-actions">
                        <button className="btn-icon" onClick={() => handleEditBirthday(birthday)} title="Edit">✏️</button>
                        <button className="btn-icon btn-danger" onClick={() => handleDeleteBirthday(birthday.id)} title="Delete">🗑️</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Contest Modal */}
      {showContestModal && (
        <div className="modal-overlay" onClick={() => setShowContestModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏆 Create New Contest</h2>
              <button className="modal-close" onClick={() => setShowContestModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateContest}>
              <div className="form-group">
                <label>Contest Name</label>
                <input type="text" value={contestForm.name} onChange={e => setContestForm({...contestForm, name: e.target.value})} placeholder="e.g., Spring 2026 Review Contest" required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={contestForm.description} onChange={e => setContestForm({...contestForm, description: e.target.value})} placeholder="Contest details..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" value={contestForm.start_date} onChange={e => setContestForm({...contestForm, start_date: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={contestForm.end_date} onChange={e => setContestForm({...contestForm, end_date: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>🥇 1st Prize</label>
                  <input type="text" value={contestForm.prize_1st} onChange={e => setContestForm({...contestForm, prize_1st: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>🥈 2nd Prize</label>
                  <input type="text" value={contestForm.prize_2nd} onChange={e => setContestForm({...contestForm, prize_2nd: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>🥉 3rd Prize</label>
                  <input type="text" value={contestForm.prize_3rd} onChange={e => setContestForm({...contestForm, prize_3rd: e.target.value})} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowContestModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Contest</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Birthday Modal */}
      {showBirthdayModal && (
        <div className="modal-overlay" onClick={() => setShowBirthdayModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎂 {editingBirthday ? 'Edit' : 'Add'} Birthday</h2>
              <button className="modal-close" onClick={() => setShowBirthdayModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateBirthday}>
              <div className="form-group">
                <label>Employee Name</label>
                <input type="text" value={birthdayForm.employee_name} onChange={e => setBirthdayForm({...birthdayForm, employee_name: e.target.value})} placeholder="Full name" required />
              </div>
              <div className="form-group">
                <label>Birthday Date</label>
                <input type="date" value={birthdayForm.birthday_date} onChange={e => setBirthdayForm({...birthdayForm, birthday_date: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Message (optional)</label>
                <input type="text" value={birthdayForm.message} onChange={e => setBirthdayForm({...birthdayForm, message: e.target.value})} placeholder="e.g., Happy Birthday! 🎉" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Display From</label>
                  <input type="date" value={birthdayForm.display_from} onChange={e => setBirthdayForm({...birthdayForm, display_from: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Display Until</label>
                  <input type="date" value={birthdayForm.display_until} onChange={e => setBirthdayForm({...birthdayForm, display_until: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select value={birthdayForm.priority} onChange={e => setBirthdayForm({...birthdayForm, priority: e.target.value})}>
                  <option value="normal">Normal</option>
                  <option value="high">High (Featured)</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowBirthdayModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">{editingBirthday ? 'Save Changes' : 'Add Birthday'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 30px;
        }
        .stat-card {
          background: #1E1E1E;
          border-radius: 16px;
          padding: 25px;
          text-align: center;
          box-shadow: 0 0 15px rgba(212, 169, 78, 0.12), 0 0 30px rgba(212, 169, 78, 0.06);
          border: 2px solid transparent;
        }
        .stat-yellow { border-color: #D4A94E; }
        .stat-green { border-color: #22c55e; }
        .stat-blue { border-color: #3b82f6; }
        .stat-pink { border-color: #ec4899; }
        .stat-icon { font-size: 40px; margin-bottom: 10px; }
        .stat-value { font-size: 42px; font-weight: 700; color: #FFFFFF; }
        .stat-label { font-size: 14px; color: #A0A0A0; margin-top: 5px; }
        .tab-navigation {
          display: flex;
          gap: 10px;
          margin-bottom: 25px;
          border-bottom: 2px solid #2A2A2A;
          padding-bottom: 10px;
        }
        .tab-btn {
          padding: 12px 24px;
          border: none;
          background: #1E1E1E;
          color: #A0A0A0;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tab-btn.active { background: #D4A94E; color: white; }
        .tab-btn:hover:not(.active) { background: #2A2A2A; }
        .active-contest-card {
          background: linear-gradient(135deg, rgba(212,169,78,0.1), rgba(184,150,62,0.15));
          border-radius: 16px;
          padding: 25px 30px;
          margin-bottom: 25px;
          border: 2px solid #D4A94E;
        }
        .contest-info h3 { font-size: 24px; color: #D4A94E; margin-bottom: 10px; }
        .contest-info p { color: #E0E0E0; margin-bottom: 15px; }
        .contest-meta { display: flex; gap: 30px; color: #D4A94E; margin-bottom: 15px; }
        .prize-info { display: flex; gap: 25px; font-weight: 600; }
        .leaderboard-section h3 { margin-bottom: 20px; color: #FFFFFF; }
        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: #1E1E1E;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }
        .data-table th, .data-table td {
          padding: 14px 16px;
          text-align: left;
          border-bottom: 1px solid #2A2A2A;
          color: #E0E0E0;
        }
        .data-table th { background: #141414; font-weight: 600; color: #A0A0A0; }
        .rank-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #2A2A2A;
          font-weight: 600;
          color: #E0E0E0;
        }
        .rank-1 { background: linear-gradient(135deg, #D4A94E, #C49A3E); }
        .rank-2 { background: linear-gradient(135deg, #c0c0c0, #a8a8a8); }
        .rank-3 { background: linear-gradient(135deg, #cd7f32, #a56627); }
        .slug-code {
          background: #2A2A2A;
          padding: 4px 10px;
          border-radius: 6px;
          font-family: monospace;
          font-size: 13px;
          color: #E0E0E0;
        }
        .badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }
        .badge-green { background: rgba(34,197,94,0.1); color: #4ADE80; }
        .badge-yellow { background: rgba(212,169,78,0.1); color: #D4A94E; }
        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .status-pending { background: rgba(212,169,78,0.1); color: #D4A94E; }
        .status-verified { background: rgba(34,197,94,0.1); color: #4ADE80; }
        .status-rejected { background: rgba(239,68,68,0.1); color: #F87171; }
        .attribution-select {
          padding: 6px 10px;
          border-radius: 6px;
          border: 1px solid #2A2A2A;
          background: #141414;
          color: white;
          font-size: 13px;
        }
        .review-text { max-width: 300px; overflow: hidden; text-overflow: ellipsis; color: #E0E0E0; }
        .birthday-cards {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }
        .birthday-card {
          background: #1E1E1E;
          border-radius: 16px;
          padding: 25px;
          display: flex;
          align-items: flex-start;
          gap: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
          border: 2px solid #2A2A2A;
          transition: all 0.2s;
        }
        .birthday-card:hover { border-color: #ec4899; transform: translateY(-2px); }
        .birthday-today {
          background: linear-gradient(135deg, rgba(236,72,153,0.08), rgba(236,72,153,0.12));
          border-color: #ec4899;
        }
        .birthday-emoji { font-size: 50px; }
        .birthday-info { flex: 1; }
        .birthday-info h4 { font-size: 20px; margin-bottom: 8px; color: #FFFFFF; }
        .birthday-date { color: #A0A0A0; font-size: 14px; }
        .birthday-message { color: #6B6B6B; font-style: italic; margin-top: 8px; }
        .today-badge {
          display: inline-block;
          background: #ec4899;
          color: white;
          padding: 4px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          margin-top: 10px;
          animation: pulse 1s infinite;
        }
        .birthday-actions { display: flex; flex-direction: column; gap: 8px; }
        .btn-icon {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 8px;
          background: #2A2A2A;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
        }
        .btn-icon:hover { background: #3A3A3A; }
        .btn-icon:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-icon.btn-danger:hover { background: rgba(239,68,68,0.1); }
        .empty-state { text-align: center; padding: 60px; color: #A0A0A0; }
        .empty-state h3 { margin-top: 15px; color: #6B6B6B; }
        .empty-cell { text-align: center; color: #6B6B6B; padding: 40px !important; }
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: #1E1E1E;
          border-radius: 20px;
          width: 100%;
          max-width: 550px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid #2A2A2A;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 25px;
          border-bottom: 1px solid #2A2A2A;
        }
        .modal-header h2 { font-size: 22px; color: #FFFFFF; }
        .modal-close {
          width: 36px;
          height: 36px;
          border: none;
          background: #2A2A2A;
          color: #E0E0E0;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
        }
        .modal-content form { padding: 25px; }
        .form-group { margin-bottom: 20px; }
        .form-group label { display: block; margin-bottom: 8px; font-weight: 500; color: #A0A0A0; }
        .form-group input, .form-group textarea, .form-group select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #2A2A2A;
          border-radius: 10px;
          font-size: 15px;
          background: #141414;
          color: white;
          transition: border-color 0.2s;
        }
        .form-group input:focus, .form-group textarea:focus, .form-group select:focus {
          outline: none;
          border-color: #D4A94E;
        }
        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 20px;
          border-top: 1px solid #2A2A2A;
          margin-top: 20px;
        }
        .btn-primary {
          padding: 12px 24px;
          background: #D4A94E;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
        }
        .btn-secondary {
          padding: 12px 24px;
          background: #2A2A2A;
          color: #E0E0E0;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
        }
        .btn-sm {
          padding: 6px 14px;
          font-size: 13px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
        }
        .btn-success { background: rgba(34,197,94,0.15); color: #4ADE80; }
        .reviews-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .reviews-header h3 { color: #FFFFFF; }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default CelebrationsBoard;
